/**
 * Public, unauthenticated bill view — the page a customer lands on from the
 * signed share link (`/bill/:token`, `GET /billing/b/{token}` on the API).
 * Outside `RequireAuth` in App.tsx: no login wall, matching the API route it
 * calls. Printable — the toolbar hides under `print:` and nothing else needs
 * a page-break rule at this length.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import Brand from "@/components/Brand";
import { useAuth } from "@/auth/AuthContext";
import { BillInsights } from "@/features/billing/BillInsights";
import { billStatusLabel } from "@/features/billing/format";
import { formatPKR } from "@/lib/currency";
import { formatWhen } from "@/lib/time";
import { localityLabel } from "@/lib/localities";
import { usePublicBill } from "@/features/billing/queries";

const STATUS_CLASS: Record<string, string> = {
  issued: "bg-brand/15 text-brand",
  paid: "bg-status-ok/15 text-status-ok",
  overdue: "bg-status-critical/15 text-status-critical",
  disputed: "bg-status-critical/15 text-status-critical",
};

function Row({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-1.5 text-sm ${emphasis ? "font-semibold text-fg" : "text-fg-muted"}`}>
      <span>{label}</span>
      <span className={emphasis ? "text-fg" : undefined}>{value}</span>
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center bg-bg px-4">{children}</div>;
}

export function PublicBillPage() {
  const { t, i18n } = useTranslation();
  const { orgName } = useAuth();
  const { token } = useParams<{ token: string }>();
  const { data: bill, isLoading, isError } = usePublicBill(token);

  if (isLoading) {
    return (
      <Centered>
        <p className="text-sm text-fg-subtle">…</p>
      </Centered>
    );
  }

  if (isError || !bill) {
    return (
      <Centered>
        <div className="max-w-sm rounded-lg border border-border bg-bg-elevated p-6 text-center shadow-sm">
          <p className="text-sm font-semibold text-fg">{t("billing.linkExpiredTitle")}</p>
          <p className="mt-1 text-sm text-fg-subtle">{t("billing.linkExpiredHelp")}</p>
        </div>
      </Centered>
    );
  }

  return (
    <div className="min-h-screen bg-bg px-4 py-8 text-fg">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center justify-between print:hidden">
          <Brand variant="lockup" size={48} />
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-bg-subtle"
          >
            {t("actions.print")}
          </button>
        </div>

        <div className="rounded-lg border border-border bg-bg-elevated shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div>
              <h1 className="text-base font-semibold text-fg">{t("billing.billNumber", { id: bill.id })}</h1>
              {orgName && <p className="text-xs text-fg-subtle">{orgName}</p>}
            </div>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                STATUS_CLASS[bill.status] ?? "bg-fg-muted/15 text-fg-muted"
              }`}
            >
              {billStatusLabel(t, bill.status)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 border-b border-border px-6 py-4 text-sm sm:grid-cols-3">
            <div>
              <div className="text-xs text-fg-muted">{t("billing.houseAccount")}</div>
              <div className="font-medium">{bill.house_id ?? "—"}</div>
              {bill.locality && (
                <div className="text-xs text-fg-muted">
                  {localityLabel(bill.locality, i18n.language)}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs text-fg-muted">{t("billing.billingPeriod")}</div>
              <div className="font-medium">
                {bill.period_start} → {bill.period_end}
              </div>
            </div>
            <div>
              <div className="text-xs text-fg-muted">{t("billing.dueDate")}</div>
              <div className={`font-medium ${bill.status === "overdue" ? "text-status-critical" : ""}`}>
                {bill.due_date ?? "—"}
              </div>
            </div>
          </div>

          <div className="border-b border-border px-6 py-4">
            <div className="text-xs text-fg-muted">{t("localities.consumption")}</div>
            <div className="text-lg font-semibold">
              {bill.consumption_m3.toFixed(3)} {t("units.m3")}
            </div>
            {bill.read_quality !== "actual" && (
              <div className="text-xs text-status-warn">{t("billing.estimatedRead")}</div>
            )}
          </div>

          {bill.lines.length > 0 && (
            <div className="border-b border-border px-6 py-4">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {t("billing.tariffBreakdown")}
              </h2>
              <table className="w-full text-sm">
                <thead className="text-xs text-fg-muted">
                  <tr>
                    <th className="py-1 text-left font-medium">{t("billing.block")}</th>
                    <th className="py-1 text-right font-medium">{t("billing.consumed")}</th>
                    <th className="py-1 text-right font-medium">{t("billing.amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.lines.map((li, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-1">{li.block_label}</td>
                      <td className="py-1 text-right text-fg-muted">{li.consumed_qty.toFixed(3)}</td>
                      <td className="py-1 text-right font-medium">{formatPKR(li.amount, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-6 py-4">
            <Row label={t("billing.waterCharge")} value={formatPKR(bill.water_charge, 2)} />
            <Row label={t("billing.sewerageCharge")} value={formatPKR(bill.sewerage_charge, 2)} />
            {bill.fixed_charge > 0 && <Row label={t("billing.fixedCharge")} value={formatPKR(bill.fixed_charge, 2)} />}
            {bill.tax_charge > 0 && <Row label={t("billing.taxCharge")} value={formatPKR(bill.tax_charge, 2)} />}
            {bill.adjustments !== 0 && <Row label={t("billing.adjustments")} value={formatPKR(bill.adjustments, 2)} />}
            <div className="my-1.5 border-t border-border" />
            <Row label={t("billing.subtotal")} value={formatPKR(bill.total_amount, 2)} emphasis />
            {bill.previous_balance > 0 && (
              <Row label={t("billing.previousBalance")} value={formatPKR(bill.previous_balance, 2)} />
            )}
            <div className="my-1.5 border-t border-border" />
            <Row label={t("billing.totalDue")} value={formatPKR(bill.total_due, 2)} emphasis />
            {bill.paid_amount != null && <Row label={t("billing.paidAmount")} value={formatPKR(bill.paid_amount, 2)} />}
          </div>

          {(bill.issued_at || bill.paid_at) && (
            <div className="border-t border-border px-6 py-3 text-xs text-fg-subtle">
              {bill.issued_at && <p>{t("billing.issuedOn")}: {formatWhen(bill.issued_at, i18n.language) ?? bill.issued_at}</p>}
              {bill.paid_at && <p>{t("billing.paidOn")}: {formatWhen(bill.paid_at, i18n.language) ?? bill.paid_at}</p>}
            </div>
          )}
        </div>

        {bill.sensor_id && <BillInsights sensorId={bill.sensor_id} />}
      </div>
    </div>
  );
}

export default PublicBillPage;
